import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getToken, setToken, clearToken, getRef, REF_STORE, brandKey } from "@/lib/appresse";
import { toast } from "@/components/ui/sonner";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

const USER_CACHE = "appresse_user";
const cacheUser = (u) => { try { localStorage.setItem(USER_CACHE, JSON.stringify(u)); } catch { /* ignore */ } };
const getCachedUser = () => { try { return JSON.parse(localStorage.getItem(USER_CACHE) || "null"); } catch { return null; } };
const clearCachedUser = () => localStorage.removeItem(USER_CACHE);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anonymous, object = logged in
  const [favorites, setFavorites] = useState(new Set());
  const [favBrands, setFavBrands] = useState(new Set()); // journaux suivis (toutes leurs éditions = cœur rouge)

  const refreshFavorites = useCallback(async () => {
    try {
      const d = await api.myFavorites();
      const eds = d.editions || [];
      setFavorites(new Set(d.ids || eds.map((e) => e.id)));
      setFavBrands(new Set(eds.map((e) => brandKey(e.title)).filter(Boolean)));
    } catch {
      setFavorites(new Set());
      setFavBrands(new Set());
    }
  }, []);

  useEffect(() => {
    (async () => {
      if (getToken()) {
        // Rester connecté : on affiche tout de suite l'utilisateur en cache,
        // puis on rafraîchit en arrière-plan.
        const cached = getCachedUser();
        if (cached) { setUser(cached); refreshFavorites(); }
        try {
          const u = await api.me();
          setUser(u);
          cacheUser(u);
          await refreshFavorites();
        } catch (e) {
          // Ne déconnecter QUE si le jeton est réellement invalide/expiré (401).
          // Une simple panne réseau/serveur ne doit jamais déconnecter le client.
          if (e?.response?.status === 401) {
            clearToken();
            clearCachedUser();
            setUser(false);
          } else if (!cached) {
            setUser(false);
          }
        }
      } else {
        setUser(false);
      }
    })();
  }, [refreshFavorites]);

  const login = async (email, password) => {
    const d = await api.login({ email, password });
    setToken(d.access_token);
    setUser(d.user);
    cacheUser(d.user);
    refreshFavorites(); // en arrière-plan : ne bloque pas l'entrée
    return d.user;
  };
  const register = async (email, password, extra = {}) => {
    const ref = getRef();
    const pros = localStorage.getItem("appresse_pros") || "";
    const d = await api.register({ email, password, ...extra, ...(ref ? { ref } : {}), ...(pros ? { prospect_token: pros } : {}) });
    if (pros) localStorage.removeItem("appresse_pros");
    setToken(d.access_token);
    setUser(d.user);
    cacheUser(d.user);
    if (ref) localStorage.removeItem(REF_STORE);
    refreshFavorites(); // en arrière-plan
    return d.user;
  };
  const logout = () => {
    clearToken();
    clearCachedUser();
    setUser(false);
    setFavorites(new Set());
    setFavBrands(new Set());
  };
  const refreshUser = useCallback(async () => {
    try {
      const u = await api.me();
      setUser(u);
      cacheUser(u);
      return u;
    } catch {
      return null;
    }
  }, []);
  const toggleFavorite = async (id, title = "") => {
    // Optimistic: flip the heart instantly, reconcile with the server after.
    let willFav = false;
    setFavorites((prev) => {
      const n = new Set(prev);
      if (n.has(id)) { n.delete(id); willFav = false; } else { n.add(id); willFav = true; }
      return n;
    });
    // When favoriting, light up every edition of this journal straight away.
    if (willFav && title) {
      setFavBrands((prev) => new Set(prev).add(brandKey(title)));
    }
    try {
      const d = await api.toggleFavorite(id);
      setFavorites((prev) => {
        const n = new Set(prev);
        d.favorited ? n.add(id) : n.delete(id);
        return n;
      });
      // Recompute followed journals accurately (a brand stays red if any of its
      // other issues is still favorited).
      refreshFavorites();
      return d.favorited;
    } catch (e) {
      setFavorites((prev) => {
        const n = new Set(prev);
        willFav ? n.delete(id) : n.add(id);
        return n;
      });
      toast.error("Action impossible, réessayez");
      return !willFav;
    }
  };

  // A revue is "favorite" if itself is favorited OR its journal is followed.
  const isFavorite = useCallback(
    (edition) => favorites.has(edition.id) || (edition.title ? favBrands.has(brandKey(edition.title)) : false),
    [favorites, favBrands]
  );

  return (
    <AuthCtx.Provider
      value={{ user, favorites, favBrands, isFavorite, login, register, logout, toggleFavorite, refreshFavorites, refreshUser }}
    >
      {children}
    </AuthCtx.Provider>
  );
}
